import { action, makeAutoObservable, observable } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import { GeneratorAPI, IntegrationAlmAPI } from '~/services/api';
import { format } from 'date-fns';
import FileSaver from 'file-saver';
import store from '~/stores';
import { jsPDF } from 'jspdf';
import { alert } from '~/utils';

export default class EpicStore implements ItemStore {
  @observable
  issueType: 'Epic' = 'Epic';

  @observable
  selected?: Epic.Type;

  @observable
  items: Epic.Type[] = [];

  @observable
  generatedItems: GeneratedItem[] = [];

  @observable
  isExporting: boolean = false;

  @observable
  isRefining: boolean = false;

  @observable
  refineInput: Epic.RefineInput = {
    title: '',
    description: '',
    suggestion: '',
    rules: '',
  };

  @observable
  refineOutput: Epic.RefineOutput = {
    summary: '',
    description: '',
    feedback: 0,
  }

  constructor() {
    makeAutoObservable(this);
    makePersistable(this, {
      name: 'EpicStore',
      properties: ['selected', 'items'],
      storage: window.localStorage,
    });
    this.saveItemsLocalStorage();
  }

  @action
  select(id: number): void {
    this.selected = this.items.find(item => item.id === id);
  }

  @action
  add(item: Epic.Type): void {
    this.items.push(item);
  };

  @action
  edit(item: Epic.Type): void {
    const index = this.items.findIndex(existingItem => existingItem.id === item.id);
    this.items[index] = item;
  };

  @action
  remove(id: number): void {
    this.items = this.items.filter(item => item.id !== id);
    this.selected = undefined;
  };

  @action
  resetItems(): void {
    this.items = [];
  };

  @action
  setFeedback = async (id: number, feedback: number): Promise<void> => {
    const indexItem = this.items.findIndex(item => item.id === id);
    this.items[indexItem].feedback = feedback;
    if (this.generatedItems.length > 0) {
      const indexGeneratedItem = this.generatedItems.findIndex(item => item.id === id);
      if (indexGeneratedItem !== -1) {
        this.generatedItems[indexGeneratedItem].feedback = feedback;
      }
    }
    await this.onEpicFeedback(this.items[indexItem]);
  };

  @action
  onEpic = async (data: Epic.Forms): Promise<any> => {
    const rsp = await GeneratorAPI.onEpic(data);
    return rsp;
  };

  @action
  getEpic = async (data: EpicType.GetResponse): Promise<boolean> => {
    this.generatedItems = [];
    const rsp = await GeneratorAPI.getEpic(data);
    console.log(rsp[0].content)
    if (rsp.length === 0) {
      return false;
    } else {
      rsp[0].content.map((item) => {
        let itemId = store.itemId.generate();
        this.add({
          id: itemId,
          artifact_id: rsp[0]._id,
          item_id: item.id,
          feedback: item.feedback,
          content: {
            summary: item.summary,
            description: item.description ? item.description : '',
          },
          project: store.project.selected!,
          forms: {
            application: rsp[0].title,
            objective: rsp[0].description,
            amount: rsp[0].content.length,
          },
          type: 'custom',
        });
        this.generatedItems.push({
          project: store.project.selected!,
          id: itemId,
          feedback: item.feedback,
          content: {
            summary: item.summary,
            description: item.description ? item.description : ''
          },
        });
      });
      return true;
    }
  };

  @action
  updateEpic = async (epic: Epic.Type): Promise<any> => {
    try {
      const response = await GeneratorAPI.updateEpic(epic);
      return response.data;
    } catch (error: any) {
      console.error('Erro ao atualizar épico:', error);
      throw error;
    }
  };

  @action
  onEpicFeedback = async (epic: Epic.Type): Promise<any> => {
    const rsp = await GeneratorAPI.onEpicFeedback(epic);
    return rsp;
  };

  @action
  saveItemsLocalStorage(): void {
    const epicStore = localStorage.getItem('EpicStore');
    if (epicStore) {
      const epicStoreJson = JSON.parse(epicStore);
      epicStoreJson.items = this.items;
      localStorage.setItem('EpicStore', JSON.stringify(epicStoreJson));
    }
  };

  @action
  exportToPDF = (): void => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(12);

      const selectedProject = store.project.selected;
      const projectName = store.project.selected?.name || '';
      const projectId = selectedProject?.id;
      const date = new Date();
      const formattedDate = format(date, 'dd-MM-yyyy HH:mm:ss');

      const filteredItems = this.items.filter(item =>
        projectId ? item.project.id === projectId && item.feedback === 2 : false
      )

      if (filteredItems.length === 0) {
        doc.text("Nenhum épico encontrado.", 10, 10);
      } else {
        filteredItems.forEach((item, index) => {
          const startY = 10 + (index * 30);

          const summaryLines = doc.splitTextToSize(`${item.content.summary}`, 190);
          doc.setFont('helvetica', 'bold');
          doc.text(summaryLines, 10, startY + 8);
          const descriptionLines = doc.splitTextToSize(`Descrição: ${item.content.description}`, 190);
          doc.setFont('helvetica', 'normal');
          doc.text(descriptionLines, 10, startY + 8 + summaryLines.length * 8);
        });
      }

      const pdfFileName = `${projectName}_${formattedDate.replace(/[:\s]/g, '-')}.pdf`;
      doc.save(pdfFileName);

      alert({
        message: 'Épico exportado com sucesso!',
        type: 'success',
        position: 'top-center',
      });
    } catch (error) {
      alert({
        message: `Erro ao exportar épico: ${(error as Error).message}`,
        type: 'error',
        position: 'top-center',
      });
    }
  };

  @action
  exportToJira = async (): Promise<boolean> => {
    this.isExporting = true;
    const issues: Jira.ExportRequest[] = this.items
      .filter(item => item.feedback === 2)
      .map(item => ({
        project: String(store.project.selected!.id),
        summary: item.content.summary,
        description: item.content.description,
        issuetype: {
          hierarchyLevel: 1,
          name: "Epic",
        },
      }));

    try {
      const response = await GeneratorAPI.exportItemsToJira({ issues });
      if (response.status === 201) {
        alert({
          message: 'Épicos exportados para o Jira com sucesso!',
          type: 'success',
          position: 'top-center',
        });
        return true;
      } else {
        alert({
          message: `Erro ao exportar Épicos para o Jira: ${response.status} | ${response.statusText}`,
          type: 'error',
          position: 'top-center',
        });
        return false;
      }
    } finally {
      this.isExporting = false;
    }
  };

  @action
  exportItemsToTxt(): void {
    const projectId = store.project.selected?.id;
    const content = this.items
      .filter(item => projectId ? item.project.id === projectId && item.feedback === 2 : false)
      .map(item => {
        if (item.content) {
          return `${item.content.summary}\nDescrição: ${item.content.description}`;
        } else {
          return "Conteúdo não disponível";
        }
      }).join('\n\n');

    const projectName = store.project.selected?.name || '';
    const date = new Date();
    const formattedDate = format(date, 'dd-MM-yyyy HH:mm:ss');

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    try {
      FileSaver.saveAs(blob, `${projectName}_${formattedDate}.txt`);
      alert({
        message: 'Épico exportado com sucesso!',
        type: 'success',
        position: 'top-center',
      });
    } catch (error) {
      alert({
        message: `Erro ao exportar épico: ${(error as Error).message}`,
        type: 'error',
        position: 'top-center',
      });
    }
  };

  @action
  async getJiraEpics(): Promise<boolean> {
    if (!!store.project.selected && store.project.selected.type === 'jira') {
      const rsp = await IntegrationAlmAPI.getEpics(String(store.project.selected.id), 'Epic');
      this.parseJiraEpics(rsp);
      if (rsp.length === 0) {
        return false;
      }
      else {
        return true;
      };
    };
    return false;
  };

  @action
  parseJiraEpics(apiResponse: any[]): void {
    apiResponse.forEach((epic: any) => {
      const item: Epic.Type = {
        id: store.itemId.generate(),
        item_id: epic.id,
        feedback: 0,
        content: {
          summary: epic.fields.summary,
          description: epic.fields.description
        },
        project: store.project.selected!,
        type: 'jira',
        jira: {
          key: epic.key,
          fields: {
            summary: epic.fields.summary,
            description: epic.fields.description
          }
        }
      };
      if (!this.items.some(existingItem => existingItem.jira?.key === item.jira?.key)) {
        this.items.push(item);
      }
    });
  };

  @action
  exportToAzure = async (): Promise<boolean> => {
    this.isExporting = true;
    const workitems: Azure.ExportRequest = {
      project: String(store.project.selected!.id),
      work_items: this.items
        .filter(item => item.feedback === 2)
        .map(item => ({
          work_item_type: 'Epic',
          title: item.content.summary,
          description: item.content.description
        }))
    }

    try {
      const response = await GeneratorAPI.exportItemsToAzureDevOps({ workitems });
      if (response.status === 201) {
        alert({
          message: 'Épicos exportados para o Azure com sucesso!',
          type: 'success',
          position: 'top-center',
        });
        return true;
      } else {
        alert({
          message: `Erro ao exportar Épicos para o Azure: ${response.status} | ${response.statusText}`,
          type: 'error',
          position: 'top-center',
        });
        return false;
      }
    } finally {
      this.isExporting = false;
    }

  }

  @action
  async getAzureEpics(): Promise<boolean> {
    if (!!store.project.selected && store.project.selected.type === 'azure') {
      const rsp = await IntegrationAlmAPI.getAzureWorkItems(String(store.project.selected.id), 'Epic');
      this.parseAzureEpics(rsp);
      if (rsp.length === 0) {
        return false;
      }
      else {
        return true;
      };
    };
    return false;
  };

  @action
  parseAzureEpics(apiResponse: any[]): void {
    apiResponse.forEach((epic: any) => {
      const item: Epic.Type = {
        id: store.itemId.generate(),
        item_id: epic.id,
        feedback: 0,
        content: {
          summary: epic.title,
          description: epic.description || ''
        },
        project: store.project.selected!,
        type: 'azure',
        azure: {
          id: epic.id,
          fields: {
            title: epic.title,
            description: epic.description
          }
        }
      };
      if (!this.items.some(existingItem => existingItem.azure?.id === item.azure?.id)) {
        this.items.push(item);
      }
    });
  };

  @action
  orderEpics(): void {
    const order = ['azure', 'jira', 'custom'];
    this.items.sort((a, b) => {
      return order.indexOf(a.type) - order.indexOf(b.type);
    });
  };

  @action
  refine = async (): Promise<void> => {
    this.isRefining = true;
    const response = await GeneratorAPI.refineEpic({
      title: this.refineInput.title,
      description: this.refineInput.description,
      suggestion: this.refineInput.suggestion || '',
      rules: this.refineInput.rules || '',
    });
    this.refineOutput = response;
    this.isRefining = false;
  };

  @action
  handleChangeRefineInput(field: keyof Epic.RefineInput, value: string): void {
    this.refineInput[field] = value;
  };

  @action
  setRefineInput(data: Partial<Epic.RefineInput>): void {
    this.refineInput = { ...this.refineInput, ...data };
  };

  @action
  exportRefinedToPDF = (): void => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(12);
      const projectName = store.project.selected?.name || '';
      const date = new Date();
      const formattedDate = format(date, 'dd-MM-yyyy HH:mm:ss');
      if (!this.refineOutput.summary && !this.refineOutput.description) {
        doc.text('Nenhum épico refinado encontrado.', 10, 10);
      } else {
        const summaryLines = doc.splitTextToSize(`${this.refineOutput.summary}`, 190);
        doc.setFont('helvetica', 'bold');
        doc.text(summaryLines, 10, 18);
        const descriptionLines = doc.splitTextToSize(`Descrição: ${this.refineOutput.description}`, 190);
        doc.setFont('helvetica', 'normal');
        doc.text(descriptionLines, 10, 18 + summaryLines.length * 8);
      }
      const pdfFileName = `${projectName}_refinado_${formattedDate.replace(/[:\s]/g, '-')}.pdf`;
      doc.save(pdfFileName);
      alert({
        message: 'Épico refinado exportado com sucesso!',
        type: 'success',
        position: 'top-center',
      });
    } catch (error) {
      alert({
        message: `Erro ao exportar épico refinado: ${(error as Error).message}`,
        type: 'error',
        position: 'top-center',
      });
    }
  };

  @action
  exportRefinedToTxt(): void {
    const projectName = store.project.selected?.name || '';
    const date = new Date();
    const formattedDate = format(date, 'dd-MM-yyyy HH:mm:ss');
    let content = '';
    if (!this.refineOutput.summary && !this.refineOutput.description) {
      content = 'Nenhum épico refinado encontrado.';
    } else {
      content = `${this.refineOutput.summary}\nDescrição: ${this.refineOutput.description}`;
    }
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    try {
      FileSaver.saveAs(blob, `${projectName}_refinado_${formattedDate}.txt`);
      alert({
        message: 'Épico refinado exportado com sucesso!',
        type: 'success',
        position: 'top-center',
      });
    } catch (error) {
      alert({
        message: `Erro ao exportar épico refinado: ${(error as Error).message}`,
        type: 'error',
        position: 'top-center',
      });
    }
  }
};
